import React, { useState } from 'react';
import classNames from 'classnames';
import styles from '../assets/grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import textFilter from 'src/components/react-table/filters/text-filter';
import { QueryParams } from 'src/types/grid';
import { CellProps } from 'react-table';

import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';

import { IOrderExpiredReport } from 'src/types/orderExpiredReport';

import user from '../../../../../../services/user-auth';
import UserRole from '../../../../../../enumerables/user-role';
import dateFilter from 'src/components/react-table/filters/date-filter';
import { changeToZeroMoment } from '../../../../../../helpers/timeHelpers';
import dropDownFilter from '../../../../../../components/react-table/filters/dropdown-filter';
import { IOrderRemainingHoursReport } from '../../../../../../types/orderRemainingHoursReport';
import { IOrder } from '../../../../../../types/order';
import dropdownOrderFilter from '../../../../../../components/react-table/filters/dropdown-order-filter';
import OrderEnum from '../../../../../../enumerables/order-enum';
import { getValFromEnum } from '../../../../../../components/redux-form/utils/dropdown-values';

interface Props {
    data: Paginated<IOrderExpiredReport>;
    students: SelectObject[];
    districts: SelectObject[];
    vendors: SelectObject[];
    getData: (params: QueryParams) => void;
    getDataCsv: (params: QueryParams) => void;
    serviceTypes: SelectObject[];
}

const ServiceTypeProviderValues = OrderEnum ? OrderEnum.getEnum('ServiceTypeProvider') : {};
const valuesServiceTypeProvider: SelectObject[] = getValFromEnum(ServiceTypeProviderValues);

const getColumns = (
    dataStud: SelectObject[],
    districts: SelectObject[],
    vendors: SelectObject[],
    serviceTypes: SelectObject[],
): Column<IOrderExpiredReport>[] => [
    {
        Header: 'Order',
        accessor: 'id',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrderExpiredReport>) => {
            if (user.getRoles().includes(UserRole.ROLE_ADMIN)) {
                return <Link to={Router.generate(routes.ORDER_DETAIL, { id: original.id })}>{original.id}</Link>;
            } else {
                return original.id;
            }
        },
        Filter: textFilter<IOrderExpiredReport>(),
    },
    {
        Header: 'Order Name',
        accessor: 'name',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrderExpiredReport>) => {
            return original.name;
        },
        Filter: textFilter<IOrderExpiredReport>(),
    },
    {
        Header: 'Case Number',
        accessor: 'caseNumber',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrderExpiredReport>) => original.caseNumber,
        Filter: textFilter<IOrderExpiredReport>(),
    },
    {
        Header: 'Student',
        accessor: 'student.lastName',

        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrderExpiredReport>) =>
            original.student ? `${original.student?.firstName} ${original.student?.lastName}` : null,
        Filter: textFilter<IOrderExpiredReport>('text', 500, 'Search by Last name'),
    },
    {
        Header: 'Service',
        accessor: 'serviceType',
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrderExpiredReport>) => {
            const labelArr = [];
            if (original?.serviceType?.title) {
                labelArr.push(original.serviceType.title);
            }
            if (original?.serviceType?.cpt) {
                labelArr.push(`CPT: [${original.serviceType.cpt}]`);
            }
            const label = labelArr.join(', ');
            return label;
        },
        Filter: dropDownFilter<IOrderExpiredReport>(serviceTypes, styles.minWidth),
    },
    {
        Header: 'Service Type Provider',
        accessor: 'serviceTypeProvider',
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const valueServiceTypeProvider = valuesServiceTypeProvider.find(
                (it) => it.value === original.serviceTypeProvider,
            );
            return valueServiceTypeProvider ? valueServiceTypeProvider.label : null;
        },
        Filter: dropdownOrderFilter<IOrderExpiredReport>(valuesServiceTypeProvider),
    },
    {
        Header: 'Valid To',
        accessor: 'caseNumberValidityTo[before]',

        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrderExpiredReport>) => {
            return original.caseNumberValidityTo
                ? `${changeToZeroMoment(original.caseNumberValidityTo, 'MMM D, YYYY')}`
                : null;
        },

        Filter: dateFilter<IOrderExpiredReport>(),
    },
    {
        Header: 'Used Hours, in %',
        accessor: 'amountOfUsedHoursInPercent',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrderExpiredReport>) => `${original.amountOfUsedHoursInPercent} %`,
        Filter: textFilter<IOrderExpiredReport>(),
    },
    {
        Header: 'Order Expiration',
        accessor: 'orderExpiration[after]',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrderExpiredReport>) => {
            const date = original.orderExpiration ? original.orderExpiration.split('T')[0] : '';
            return date ? changeToZeroMoment(date, 'MMM D, YYYY') : '';
        },
        disableSortBy: true,
        Filter: dateFilter<IOrderExpiredReport>(),
    },
];

const OrderExpiredReportsGrid: React.FunctionComponent<Props> = ({
    data,
    getData,
    students,
    getDataCsv,
    districts,
    vendors,
    serviceTypes,
}: Props) => {
    const [columns, setColumns] = useState<any>([]);

    if (students.length && !columns.length && serviceTypes.length) {
        setColumns(getColumns(students, districts, vendors, serviceTypes));
    }
    return (
        <div className={classNames(styles.grid)}>
            <Grid<IOrderExpiredReport> columns={columns} getDataCsv={getDataCsv} data={data} getData={getData} />
        </div>
    );
};

export default OrderExpiredReportsGrid;
